import { ApolloError, useMutation } from '@apollo/client'
import {
    AddNewContentItemForCKE,
    SetProjectContentItemElements,
} from 'api/graphQL/mutations/projectProductFamily.mutations'
import { AddProjectContentItemInputResponse } from 'api/graphQL/types/productFamilyTypes'
import { AddProjectContentItemInputRequest } from 'components/WorkspaceEditor/types'
import { isActiveTabConsistent } from 'components/WorkspaceEditor/utils/contentItemViewNodesUtils'
import { useWorkspace2Store } from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useRef } from 'react'
import { handleFetchError } from '../utils'

/**
 * Hook for managing project content item mutations (ADD and UPDATE operations).
 *
 * Key Features:
 * - ADD: Creates new content items (handled by addProjectContentItem mutation)
 * - UPDATE: Modifies existing content items (handled by updateProjectContentItemElement mutation)
 * - Automatically refreshes track changes data after mutations
 * - Handles ID changes from server by filtering old nodes and adding new ones
 *
 * Usage:
 * - For ADD operations: Use addProjectContentItem with handleContentMutationCompleted callback
 * - For UPDATE operations: Use updateProjectContentItemElement with handleUpdateItemCompleted callback
 *
 * @returns {Object} Mutation functions
 * @returns {Function} returns.updateProjectContentItemElement - Mutation for updating existing content
 * @returns {Function} returns.addProjectContentItem - Mutation for adding new content
 */
export const useProjectContentItemElementsMutation = () => {
        // Ref to track activeTab when mutation was initiated (for race condition prevention)
    const mutationInitialActiveTabRef = useRef<number | null>(null)


    /**
     * Callback for ADD operations (new content creation).
     * Currently not implemented - handled by handleContentMutationCompleted in usePlainTextContentMutation.
     * @param data - The mutation response data
     * @todo Implement response handling for add operations if needed
     */
    const handleAddItemCompleted = useCallback(<T>(data: T) => {
        const currentActiveTab = useWorkspace2Store.getState().activeTab
        const isConsistentPartType = isActiveTabConsistent(mutationInitialActiveTabRef.current, currentActiveTab)

        mutationInitialActiveTabRef.current = null

        if (!isConsistentPartType) {
            return // tab changed, discarding result
        }

        // TODO: create a response type
        // - how to handle the response? Should we update the tree data and sync updates to "old workspace"?
    }, [])

    const [updateProjectContentItemElement] = useMutation(
        SetProjectContentItemElements(6),
        {
            fetchPolicy: 'no-cache',
            onCompleted: handleAddItemCompleted,
            onError: (error: ApolloError) =>
                handleFetchError(
                    error,
                    'WorkspaceEditor-updateProjectContentItemElement'
                ),
        }
    )

    const [addProjectContentItem] = useMutation<
        AddProjectContentItemInputResponse,
        Partial<AddProjectContentItemInputRequest>
    >(AddNewContentItemForCKE(6), {
        fetchPolicy: 'no-cache',
        onCompleted: handleAddItemCompleted,
        onError: (error: ApolloError) =>
            handleFetchError(error, 'WorkspaceEditor-addProjectContentItem'),
    })

    return {
        updateProjectContentItemElement,
        addProjectContentItem,
        mutationInitialActiveTabRef,
    }
}
